import React, { useEffect, useMemo, useState } from "react";
import { ScrollText, Search, ArrowRight } from "lucide-react";
import api from "../../api";

// Coarse buckets so ~60 action types are filterable without a giant dropdown.
const GROUP = (action = "") => {
  const a = action;
  if (a.startsWith("deal-") || a.startsWith("category") || a.startsWith("discount") || a === "coupons-clear" || a === "bin-empty") return "Deals";
  if (a.startsWith("trust-") || ["suspend", "unsuspend", "grant", "grant-pro", "revoke-pro", "verify", "verify-decide", "appeal-decide", "gift-credits", "message", "email-change", "limits", "limits-approve", "power_offer", "power_offer_revoke", "offer_package", "pin_nudge"].includes(a)) return "Accounts";
  if (a.startsWith("content-") || a.startsWith("legal-") || a.startsWith("email_template") || a.startsWith("broadcast-") || ["city_copy", "email-config"].includes(a)) return "Content";
  if (["refund", "accounting-clear", "fx-refresh", "currency"].includes(a)) return "Money";
  return "System";
};

// Friendly verb for the common action codes; falls back to a title-cased code.
const LABELS = {
  "deal-recategorize": "Recategorized deal", "deal-delete": "Deleted deal", "deal-undelete": "Restored deal",
  "deal-hide": "Hid deal", "deal-unhide": "Unhid deal", "deal-approve": "Approved deal", "deal-purge": "Purged deal",
  "delist": "Delisted business", "relist": "Relisted business", "suspend": "Suspended account",
  "unsuspend": "Unsuspended account", "verify": "Verified business", "verify-decide": "Verification decision",
  "grant": "Granted plan", "grant-pro": "Granted Pro", "revoke-pro": "Revoked Pro", "gift-credits": "Gifted credits",
  "refund": "Issued refund", "reports-clear": "Cleared reports", "backup-restore": "Restored backup",
  "backup-delete": "Deleted backup", "bin-empty": "Emptied recycle bin", "features": "Changed feature flags",
  "cache-purge": "Purged cache", "broadcast-schedule": "Scheduled broadcast", "broadcast-cancel": "Cancelled broadcast",
  "content-edit": "Edited content", "content-revert": "Reverted content", "email_template": "Edited email template",
  "email_template_reset": "Reset email template", "message": "Messaged business", "appeal-decide": "Appeal decision",
  "fx-refresh": "Refreshed FX rates", "currency": "Changed currency", "city_copy": "Edited city copy",
};
const labelFor = (a) => LABELS[a] || a.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const TABS = ["All", "Deals", "Accounts", "Content", "Money", "System"];

export const AdminActivityLog = () => {
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/admin/activity").then((r) => setRows(r.data || [])).catch(() => setRows([])); }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (tab !== "All" && GROUP(r.action) !== tab) return false;
      if (!needle) return true;
      const hay = `${r.action} ${labelFor(r.action)} ${r.adminEmail || ""} ${r.targetBusiness || ""} ${r.targetEmail || ""} ${JSON.stringify(r.meta || {})}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, tab, q]);

  if (!rows) return null;

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-activity-log">
      <h2 className="font-bold text-foreground flex items-center gap-2"><ScrollText size={18} className="text-[#16a34a]" /> Admin activity log</h2>
      <p className="text-xs text-muted-foreground mt-0.5 mb-4">Every admin action — archives, deletes, approvals, verifications, refunds and more. Newest first.</p>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="inline-flex items-center gap-1 bg-secondary rounded-full p-1">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`activity-tab-${t.toLowerCase()}`}
              className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${tab === t ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>{t}</button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search actions, admins, shops…"
            data-testid="activity-search" className="w-full text-sm border border-border rounded-xl pl-9 pr-3 py-2 bg-card outline-none focus:ring-2 focus:ring-[#16a34a]/40" />
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center" data-testid="activity-empty">No matching activity.</p>
      ) : (
        <div className="space-y-2 max-h-[28rem] overflow-y-auto no-scrollbar" data-testid="activity-list">
          {filtered.map((r) => {
            const from = r.meta?.from, to = r.meta?.to;
            const subject = r.meta?.title || r.targetBusiness || r.targetEmail || r.targetId || "";
            return (
              <div key={r.id} data-testid="activity-row" className="rounded-xl border border-border px-3 py-2 text-xs flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="rounded-full bg-secondary px-2 py-0.5 font-bold text-foreground">{GROUP(r.action)}</span>
                <span className="font-semibold text-foreground">{labelFor(r.action)}</span>
                {subject && <span className="text-muted-foreground truncate max-w-[220px]">· {subject}</span>}
                {from && to && (
                  <span className="inline-flex items-center gap-1">
                    <span className="rounded-full bg-secondary px-2 py-0.5">{from}</span>
                    <ArrowRight size={11} className="text-muted-foreground" />
                    <span className="rounded-full bg-[#16a34a]/12 text-[#15803d] px-2 py-0.5">{to}</span>
                  </span>
                )}
                <span className="text-muted-foreground ml-auto whitespace-nowrap">{r.adminEmail} · {r.createdAt ? new Date(r.createdAt).toLocaleString() : ""}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminActivityLog;
